// src/components/projecttasks/ProjectTaskKpiDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList,
  LineChart, Line,
  PieChart, Pie, Cell
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faListUl, faLock, faBriefcase, faExclamationTriangle,
  faUserSlash, faChartLine, faChartBar, faClock, faBullseye, faUsers,
} from '@fortawesome/free-solid-svg-icons';

import { MODULES } from '../../../constants/globalConstants';
import {
  readUserFromLocalStorage,
  hasPermission,
  getModulePermissionRow,
} from '../../../utils/permissions';
import type { UserDTO } from '../../auth/AuthApi';
import { API_ENDPOINTS } from '../../../apiConfig';

/* =========================
   Types (mirrors API payload)
   ========================= */
type UUID = string;

type StatusDist = { status: string; count: number; };
type CompletionByProject = { project_id: UUID; project_name: string; completion_percentage: number; };

type OverdueTask = {
  project_id: UUID; project_name: string; task_id: UUID; task_title: string;
  status: string; given_end_date: string | null; actual_end_date: string | null; days_overdue: number;
};

type UpcomingTask = {
  project_id: UUID; project_name: string; task_id: UUID; task_title: string;
  status: string; given_end_date: string | null;
};

type CostVar = {
  project_id: UUID; project_name: string;
  total_estimated_cost: number; total_actual_cost: number;
  cost_variance: number; cost_variance_percentage: number;
};

type EffortVar = {
  project_id: UUID; project_name: string;
  total_estimated_hours: number; total_actual_hours: number;
  effort_variance_hours: number;
};

type BillableSummary = {
  is_billable: boolean; task_count: number;
  total_actual_hours: number; total_estimated_hours: number;
  total_actual_cost: number; total_estimated_cost: number;
};

type MilestoneProgress = {
  project_id: UUID; project_name: string;
  total_milestones: number; completed_milestones: number;
  milestone_completion_percentage: number | null;
};

type AvgCycle = {
  project_id: UUID; project_name: string;
  avg_cycle_time_days: number; completed_tasks: number;
};

type Throughput = { month_yyyy_mm: string; completed_count: number; };

type PerAssignee = { user_id: UUID; user_name: string; open_tasks: number; total_tasks: number; };

type UnassignedSummary = {
  total_unassigned_open: number;
  overdue_unassigned_open: number;
  percent_unassigned_of_open: number;
};

type UnassignedByProject = { project_id: UUID; project_name: string; unassigned_open: number; };
type UnassignedBucket = { bucket: string; cnt: number; };
type UnassignedPriority = { priority: string; cnt: number; };
type UnassignedStatus = { status: string; cnt: number; };
type UnassignedOverdue = {
  task_id: UUID; project_id: UUID; project_name: string; title: string;
  status: string; priority: string | null;
  given_end_date: string | null; actual_end_date: string | null;
  days_unassigned: number; days_overdue: number;
};

type AtRiskTask = {
  project_id: UUID; project_name: string; task_id: UUID; task_title: string;
  status: string; given_end_date: string | null; progress: number;
};

type TaskKpiResponse = {
  task_status_distribution: StatusDist[];
  task_completion_percentage_by_project: CompletionByProject[];
  overdue_tasks: OverdueTask[];
  upcoming_due_tasks: UpcomingTask[];
  cost_variance_by_project: CostVar[];
  effort_variance_by_project: EffortVar[];
  billable_summary: BillableSummary[];
  milestones_progress_by_project: MilestoneProgress[];
  avg_cycle_time_completed_by_project: AvgCycle[];
  throughput_last_12_months: Throughput[];
  tasks_per_assignee_open_top: PerAssignee[];
  at_risk_tasks: AtRiskTask[];
  // Unassigned bundle
  unassigned_summary: UnassignedSummary;
  unassigned_by_project: UnassignedByProject[];
  unassigned_age_buckets: UnassignedBucket[];
  unassigned_overdue_list: UnassignedOverdue[];
  unassigned_by_priority: UnassignedPriority[];
  unassigned_by_status: UnassignedStatus[];
};

/* =========================
   Data helpers
   ========================= */
 
const API_DASHBOARD = `${API_ENDPOINTS.project}/project-tasks/kpi`;

const fetchTaskKpis = (tenantId?: string, tenantName?: string) =>
  axios.get<TaskKpiResponse>(`${API_DASHBOARD}/task-kpi`, { params: { tenantId, tenantName } });

const fmtINR = (v: number) => `₹${Number(v || 0).toLocaleString()}`;
const truncate = (s: string, n = 30) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s || '—');

const COLORS_SEQ = ['#4f46e5', '#06b6d4', '#22c55e', '#eab308', '#f43f5e', '#8b5cf6', '#0ea5e9', '#16a34a', '#f97316', '#ef4444'];
const RED = '#ef4444', GREEN = '#22c55e', BLUE = '#3b82f6', CYAN = '#06b6d4', VIOLET = '#8b5cf6', AMBER = '#f59e0b', SLATE = '#64748b';

/* =========================
   Small presentational bits
   ========================= */
const Card: React.FC<{ children: React.ReactNode; className?: string; }> = ({ children, className }) => (
  <div className={`bg-white border rounded-xl shadow-sm ${className || ''}`}>{children}</div>
);
const CardHeader: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode; }> = ({ title, subtitle, right }) => (
  <div className="px-4 pt-4 pb-3 flex items-center justify-between">
    <div>
      <div className="text-lg font-semibold">{title}</div>
      {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
    </div>
    {right}
  </div>
);
const KpiTile: React.FC<{ icon: any; label: string; value: React.ReactNode; tone?: string; }> = ({ icon, label, value, tone = 'slate' }) => {
  const map: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-rose-50 text-rose-700 border-rose-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div className={`rounded-xl border p-4 ${map[tone]}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center">
          <FontAwesomeIcon icon={icon} />
        </div>
        <div>
          <div className="text-xs opacity-80">{label}</div>
          <div className="text-2xl font-bold">{value}</div>
        </div>
      </div>
    </div>
  );
};

/* Custom Tooltip for charts that keeps full project name */
const NameAwareTooltip: React.FC<any> = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div className="bg-white rounded-md shadow px-3 py-2 text-sm">
      <div className="font-semibold mb-1">{p.fullName || label}</div>
      {payload.map((row: any, idx: number) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded" style={{ background: row.color || row.fill }} />
          <span className="font-medium">{row.name}:</span>
          <span>{valueFormatter ? valueFormatter(row.value) : row.value}</span>
        </div>
      ))}
    </div>
  );
};

/* =========================
   Main Component
   ========================= */
const ProjectTaskKpiDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = readUserFromLocalStorage<UserDTO>();
  const tenantId = user?.tenantId;
  const tenantName = user?.tenantName;

  // RBAC: allow via Projects or Project Tasks module
  const TASKS_MODULE: string =
    (MODULES as any).PROJECT_TASKS ?? (MODULES as any).TASKS ?? 'PROJECT_TASKS';
  const projPerm = useMemo(() => getModulePermissionRow(user || null, MODULES.PROJECTS), [user]);
  const taskPerm = useMemo(() => getModulePermissionRow(user || null, TASKS_MODULE), [user]);

  const canSeeDashboard = useMemo(() => {
    const viewOrReport =
      hasPermission(user || null, MODULES.PROJECTS, ['view', 'report'], 'any') ||
      hasPermission(user || null, TASKS_MODULE, ['view', 'report'], 'any');
    const visibleFlagProj =
      typeof projPerm?.visibleInDashboard === 'boolean' ? !!projPerm?.visibleInDashboard : true;
    const visibleFlagTask =
      typeof taskPerm?.visibleInDashboard === 'boolean' ? !!taskPerm?.visibleInDashboard : true;
    return viewOrReport && (visibleFlagProj || visibleFlagTask);
  }, [user, projPerm, taskPerm]);

  const canSeeProjectListBtn = useMemo(
    () =>
      hasPermission(user || null, MODULES.PROJECTS, ['view', 'edit', 'create'], 'any') ||
      hasPermission(user || null, TASKS_MODULE, ['view', 'edit', 'create'], 'any'),
    [user]
  );

  const [kpis, setKpis] = useState<TaskKpiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canSeeDashboard) {
      setLoading(false);
      setKpis(null);
      setError('');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetchTaskKpis(tenantId, tenantName);
        setKpis(res.data);
      } catch (e) {
        setError('Failed to load Project Task KPIs.');
      } finally {
        setLoading(false);
      }
    })();
  }, [canSeeDashboard, tenantId, tenantName]);

  /* ======== Derived / transformed datasets ======== */
  const statusData = useMemo(() =>
    (kpis?.task_status_distribution || []).map((d, i) => ({ ...d, color: COLORS_SEQ[i % COLORS_SEQ.length] }))
  , [kpis]);

  const completionProj = useMemo(() =>
    (kpis?.task_completion_percentage_by_project || []).map(r => ({
      fullName: r.project_name,
      name: truncate(r.project_name, 30),
      value: r.completion_percentage,
    }))
  , [kpis]);

  const costVar = useMemo(() =>
    (kpis?.cost_variance_by_project || []).map(r => ({
      fullName: r.project_name,
      name: truncate(r.project_name, 30),
      variancePct: r.cost_variance_percentage,
      varianceAmt: r.cost_variance,
      actual: r.total_actual_cost,
      estimated: r.total_estimated_cost,
    }))
  , [kpis]);

  const effortVar = useMemo(() =>
    (kpis?.effort_variance_by_project || []).map(r => ({
      fullName: r.project_name,
      name: truncate(r.project_name, 30),
      est: r.total_estimated_hours,
      act: r.total_actual_hours,
      variance: r.effort_variance_hours,
    }))
  , [kpis]);

  const perAssignee = useMemo(() =>
    (kpis?.tasks_per_assignee_open_top || []).map((r, i) => ({ ...r, color: COLORS_SEQ[i % COLORS_SEQ.length] }))
  , [kpis]);

  const unassignedByProject = useMemo(() =>
    (kpis?.unassigned_by_project || []).map(r => ({
      fullName: r.project_name,
      name: truncate(r.project_name, 30),
      unassigned_open: r.unassigned_open,
    }))
  , [kpis]);

  const ageBuckets = useMemo(() =>
    (kpis?.unassigned_age_buckets || []).map((r, i) => ({ ...r, color: COLORS_SEQ[i % COLORS_SEQ.length] }))
  , [kpis]);

  const billable = useMemo(() =>
    (kpis?.billable_summary || []).map(b => ({
      label: b.is_billable ? 'Billable' : 'Non-Billable',
      task_count: b.task_count,
      hours: b.total_actual_hours,
      cost: b.total_actual_cost,
    }))
  , [kpis]);

  // quick numbers
  const overdueCount = kpis?.overdue_tasks?.length ?? 0;
  const atRiskCount = kpis?.at_risk_tasks?.length ?? 0;
  const openTotal = (kpis?.task_status_distribution || [])
    .reduce((s, d) => s + (d.status === 'COMPLETED' ? 0 : d.count), 0);
  const unassignedPct = kpis?.unassigned_summary?.percent_unassigned_of_open ?? 0;

  /* ======== Unauthorized gate ======== */
  if (!canSeeDashboard) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto bg-white border rounded-xl p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full border flex items-center justify-center text-slate-600">
              <FontAwesomeIcon icon={faLock} />
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-1">Access Restricted</h2>
          <p className="text-slate-600">
            You don’t have permission to view the Project Task dashboard.
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ======== UI ======== */
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-2 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </button>
        <h1 className="text-2xl font-bold">Project Task Dashboard</h1>
        <div className="flex items-center gap-2">
          {canSeeProjectListBtn && (
            <button
              className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate('/project-tasks/list')}
            >
              <FontAwesomeIcon icon={faListUl} className="mr-2" />
              Project Task List
            </button>
          )}
        </div>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile icon={faBriefcase} label="Open Tasks" value={openTotal} tone="blue" />
        <KpiTile icon={faUserSlash} label="% Unassigned (of open)" value={`${unassignedPct?.toFixed(1)}%`} tone="amber" />
        <KpiTile icon={faExclamationTriangle} label="Overdue Tasks" value={overdueCount} tone="red" />
        <KpiTile icon={faBullseye} label="At-Risk (7d, <50%)" value={atRiskCount} tone="green" />
      </div>

      {/* Row: Status Distribution (Donut) & Tasks per Assignee (Horizontal) */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="col-span-1">
          <CardHeader title="Task Status Distribution" subtitle="All project tasks" />
          <div className="px-4 pb-4 h-80">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : statusData.length === 0 ? (
              <div className="text-slate-500">No data.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      return (
                        <div className="bg-white rounded-md shadow px-3 py-2 text-sm">
                          {payload.map((p: any, idx: number) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="inline-block w-2 h-2 rounded" style={{ background: p.payload.color }} />
                              <span className="font-medium">{p.payload.status}:</span>
                              <span>{p.value}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Pie
                    dataKey="count"
                    data={statusData}
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="col-span-2">
          <CardHeader
            title="Open Tasks per Assignee (Top 20)"
            subtitle="Primary assignees only"
            right={<div className="text-slate-500 text-xs"><FontAwesomeIcon icon={faUsers} /> Assignees</div>}
          />
          <div className="px-4 pb-4 h-80">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : perAssignee.length === 0 ? (
              <div className="text-slate-500">No assignments found.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...perAssignee].sort((a, b) => b.open_tasks - a.open_tasks)}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 80, bottom: 8 }}
                  barCategoryGap={6}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis
                    type="category"
                    dataKey="user_name"
                    tick={{ fontSize: 12 }}
                    width={120}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const row = payload[0].payload;
                      return (
                        <div className="bg-white rounded-md shadow px-3 py-2 text-sm">
                          <div className="font-semibold mb-1">{row.user_name}</div>
                          <div>Open: {row.open_tasks}</div>
                          <div>Total: {row.total_tasks}</div>
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Bar dataKey="open_tasks" name="Open Tasks" isAnimationActive={false}>
                    {perAssignee.map((r, i) => <Cell key={i} fill={r.color} />)}
                    <LabelList dataKey="open_tasks" position="right" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Row: Completion % by Project & Cost Variance by Project */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Task Completion % by Project" right={<span className="text-slate-500 text-xs"><FontAwesomeIcon icon={faChartBar} /> Higher is better</span>} />
          <div className="px-4 pb-4 h-96">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : completionProj.length === 0 ? (
              <div className="text-slate-500">No data.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...completionProj].sort((a, b) => b.value - a.value)}
                  margin={{ left: 16, right: 16, top: 8, bottom: 48 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} angle={-30} height={70} textAnchor="end" />
                  <YAxis tickFormatter={(v) => `${v}%`} width={52} />
                  <Tooltip content={<NameAwareTooltip valueFormatter={(v: number) => `${v}%`} />} />
                  <Legend />
                  <Bar dataKey="value" name="Completion %" fill={BLUE} maxBarSize={40} isAnimationActive={false}>
                    <LabelList dataKey="value" position="top" formatter={(v: any) => `${Number(v)}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Cost Variance by Project" subtitle="(Actual − Estimated) and %" right={<span className="text-slate-500 text-xs"><FontAwesomeIcon icon={faChartLine} /> Red=Over, Green=Under</span>} />
          <div className="px-4 pb-4 h-96">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : costVar.length === 0 ? (
              <div className="text-slate-500">No data.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...costVar].sort((a, b) => (b.variancePct ?? 0) - (a.variancePct ?? 0))}
                  margin={{ left: 16, right: 16, top: 8, bottom: 48 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} angle={-30} height={70} textAnchor="end" />
                  <YAxis yAxisId="left" tickFormatter={(v) => `${v}%`} width={52} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => fmtINR(v)} width={80} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const p = payload[0].payload;
                      return (
                        <div className="bg-white rounded-md shadow px-3 py-2 text-sm">
                          <div className="font-semibold mb-1">{p.fullName || label}</div>
                          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded" style={{ background: (p.variancePct ?? 0) >= 0 ? RED : GREEN }}></span><span className="font-medium">Variance %:</span><span>{(p.variancePct ?? 0).toFixed(1)}%</span></div>
                          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded" style={{ background: AMBER }}></span><span className="font-medium">Variance Amt:</span><span>{fmtINR(p.varianceAmt)}</span></div>
                          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded" style={{ background: CYAN }}></span><span className="font-medium">Actual:</span><span>{fmtINR(p.actual)}</span></div>
                          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded" style={{ background: SLATE }}></span><span className="font-medium">Estimated:</span><span>{fmtINR(p.estimated)}</span></div>
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  {/* Variance % with dynamic color via <Cell> */}
                  <Bar yAxisId="left" dataKey="variancePct" name="Variance %" maxBarSize={38} isAnimationActive={false}>
                    {costVar.map((d, i) => (
                      <Cell key={i} fill={(d.variancePct ?? 0) >= 0 ? RED : GREEN} />
                    ))}
                    <LabelList dataKey="variancePct" position="top" formatter={(v: any) => `${Number(v).toFixed(1)}%`} />
                  </Bar>
                  {/* Variance Amount */}
                  <Bar yAxisId="right" dataKey="varianceAmt" name="Variance Amt" fill={AMBER} maxBarSize={38}>
                    <LabelList dataKey="varianceAmt" position="top" formatter={(v: any) => fmtINR(Number(v))} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Row: Effort variance & Billable vs Non-Billable */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Effort Variance by Project (Hours)" />
          <div className="px-4 pb-4 h-96">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : effortVar.length === 0 ? (
              <div className="text-slate-500">No data.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...effortVar].sort((a, b) => (b.variance ?? 0) - (a.variance ?? 0))}
                  margin={{ left: 16, right: 16, top: 8, bottom: 48 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} angle={-30} height={70} textAnchor="end" />
                  <YAxis width={52} />
                  <Tooltip content={<NameAwareTooltip />} />
                  <Legend />
                  <Bar dataKey="est" name="Estimated Hrs" fill={SLATE} maxBarSize={36} />
                  <Bar dataKey="act" name="Actual Hrs" fill={CYAN} maxBarSize={36} />
                  {/* Variance with dynamic color via <Cell> */}
                  <Bar dataKey="variance" name="Variance Hrs" maxBarSize={36} isAnimationActive={false}>
                    {effortVar.map((d, i) => (
                      <Cell key={i} fill={(d.variance ?? 0) >= 0 ? RED : GREEN} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Billable vs Non-Billable" subtitle="Counts / Cost / Hours" />
          <div className="px-4 pb-4 h-96">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : billable.length === 0 ? (
              <div className="text-slate-500">No data.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-full">
                {/* Tasks count */}
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={billable} margin={{ top: 12, left: 8, right: 8, bottom: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="task_count" name="Tasks" fill={BLUE} maxBarSize={40}>
                        <LabelList dataKey="task_count" position="top" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Cost */}
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={billable} margin={{ top: 12, left: 8, right: 8, bottom: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis tickFormatter={(v) => fmtINR(v)} width={80} />
                      <Tooltip formatter={(v: any) => fmtINR(Number(v))} />
                      <Legend />
                      <Bar dataKey="cost" name="Actual Cost" fill={AMBER} maxBarSize={40}>
                        <LabelList dataKey="cost" position="top" formatter={(v: any) => fmtINR(Number(v))} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {/* Hours */}
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={billable} margin={{ top: 12, left: 8, right: 8, bottom: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="hours" name="Actual Hours" fill={CYAN} maxBarSize={40}>
                        <LabelList dataKey="hours" position="top" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Row: Overdue & At-Risk lists */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Overdue Tasks" right={<span className="text-rose-600 text-xs"><FontAwesomeIcon icon={faClock} /> Sorted by days overdue</span>} />
          <div className="px-4 pb-4 overflow-x-auto">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : (kpis?.overdue_tasks?.length ?? 0) === 0 ? (
              <div className="text-slate-500">No overdue tasks 🎉</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-2 text-left">Project</th>
                    <th className="p-2 text-left">Task</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Given End</th>
                    <th className="p-2 text-left">Actual End</th>
                    <th className="p-2 text-right">Days Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis!.overdue_tasks!.map(r => (
                    <tr key={r.task_id} className="border-t">
                      <td className="p-2">{r.project_name}</td>
                      <td className="p-2">{r.task_title}</td>
                      <td className="p-2">{r.status}</td>
                      <td className="p-2">{r.given_end_date ? new Date(r.given_end_date).toLocaleDateString() : '—'}</td>
                      <td className="p-2">{r.actual_end_date ? new Date(r.actual_end_date).toLocaleDateString() : '—'}</td>
                      <td className="p-2 text-right">{r.days_overdue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="At-Risk (Next 7 days &lt; 50% progress)" />
          <div className="px-4 pb-4 overflow-x-auto">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : (kpis?.at_risk_tasks?.length ?? 0) === 0 ? (
              <div className="text-slate-500">No at-risk tasks</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-2 text-left">Project</th>
                    <th className="p-2 text-left">Task</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Due</th>
                    <th className="p-2 text-right">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis!.at_risk_tasks!.map(r => (
                    <tr key={r.task_id} className="border-t">
                      <td className="p-2">{r.project_name}</td>
                      <td className="p-2">{r.task_title}</td>
                      <td className="p-2">{r.status}</td>
                      <td className="p-2">{r.given_end_date ? new Date(r.given_end_date).toLocaleDateString() : '—'}</td>
                      <td className="p-2 text-right">{Math.round(r.progress)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>

      {/* Row: Unassigned KPIs */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card>
          <CardHeader title="Unassigned Summary" right={<span className="text-slate-500 text-xs"><FontAwesomeIcon icon={faUserSlash} /> Open only</span>} />
          <div className="px-4 pb-4">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <KpiTile icon={faUserSlash} label="Unassigned Open" value={kpis?.unassigned_summary?.total_unassigned_open ?? 0} tone="amber" />
                <KpiTile icon={faClock} label="Unassigned Overdue" value={kpis?.unassigned_summary?.overdue_unassigned_open ?? 0} tone="red" />
                <KpiTile icon={faUsers} label="% of Open" value={`${(kpis?.unassigned_summary?.percent_unassigned_of_open ?? 0).toFixed(1)}%`} tone="blue" />
              </div>
            )}
          </div>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader title="Unassigned Open by Project (Top 20)" />
          <div className="px-4 pb-4 h-80">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : unassignedByProject.length === 0 ? (
              <div className="text-slate-500">No unassigned open tasks 🎉</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...unassignedByProject].sort((a, b) => b.unassigned_open - a.unassigned_open)}
                  margin={{ left: 16, right: 16, top: 8, bottom: 48 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} angle={-30} height={70} textAnchor="end" />
                  <YAxis />
                  <Tooltip content={<NameAwareTooltip />} />
                  <Legend />
                  <Bar dataKey="unassigned_open" name="Unassigned Open" fill={AMBER} maxBarSize={40} isAnimationActive={false}>
                    <LabelList dataKey="unassigned_open" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Row: Age Buckets & Throughput */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Unassigned Age Buckets (Days since created)" />
          <div className="px-4 pb-4 h-80">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : (ageBuckets.length === 0) ? (
              <div className="text-slate-500">No data.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ageBuckets} margin={{ left: 16, right: 16, top: 8, bottom: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="cnt" name="Tasks" fill={SLATE} maxBarSize={40}>
                    <LabelList dataKey="cnt" position="top" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Throughput (Completed per Month, Last 12 months)" />
          <div className="px-4 pb-4 h-80">
            {loading ? (
              <div className="text-slate-500">Loading…</div>
            ) : ((kpis?.throughput_last_12_months?.length ?? 0) === 0) ? (
              <div className="text-slate-500">No completed tasks in last 12 months.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={kpis!.throughput_last_12_months}
                  margin={{ left: 16, right: 16, top: 8, bottom: 16 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month_yyyy_mm" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="completed_count" name="Completed" stroke={BLUE} dot />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Unassigned Overdue List */}
      <Card>
        <CardHeader title="Unassigned Overdue (Top 50)" />
        <div className="px-4 pb-4 overflow-x-auto">
          {loading ? (
            <div className="text-slate-500">Loading…</div>
          ) : (kpis?.unassigned_overdue_list?.length ?? 0) === 0 ? (
            <div className="text-slate-500">No unassigned overdue tasks.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">Project</th>
                  <th className="p-2 text-left">Task</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Priority</th>
                  <th className="p-2 text-left">Given End</th>
                  <th className="p-2 text-right">Days Unassigned</th>
                  <th className="p-2 text-right">Days Overdue</th>
                </tr>
              </thead>
              <tbody>
                {kpis!.unassigned_overdue_list!.map(r => (
                  <tr key={r.task_id} className="border-t">
                    <td className="p-2">{r.project_name}</td>
                    <td className="p-2">{r.title}</td>
                    <td className="p-2">{r.status}</td>
                    <td className="p-2">{r.priority || '—'}</td>
                    <td className="p-2">{r.given_end_date ? new Date(r.given_end_date).toLocaleDateString() : '—'}</td>
                    <td className="p-2 text-right">{r.days_unassigned}</td>
                    <td className="p-2 text-right">{r.days_overdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ProjectTaskKpiDashboard;